/**
 * Datasource local ReminderSettings: abstração + implementação localforage.
 */

import localforage from 'localforage';
import { ReminderSettings } from '@/core/domain';

const CACHE_KEY = 'retornaar:reminder_settings:v2';

export interface ReminderSettingsLocalDataSource {
  getById(id: string): Promise<ReminderSettings | null>;
  create(settings: ReminderSettings): Promise<ReminderSettings>;
  update(id: string, settings: Partial<ReminderSettings>): Promise<ReminderSettings>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

const store = localforage.createInstance({ name: 'retornaar', storeName: 'reminder_settings' });

export class ReminderSettingsLocalForageDataSource implements ReminderSettingsLocalDataSource {
  private async getMap(): Promise<Record<string, ReminderSettings>> {
    const raw = await store.getItem<Record<string, ReminderSettings>>(CACHE_KEY);
    return raw ?? {};
  }

  private async setMap(map: Record<string, ReminderSettings>): Promise<void> {
    await store.setItem(CACHE_KEY, map);
  }

  async getById(id: string): Promise<ReminderSettings | null> {
    const map = await this.getMap();
    return map[id] ?? null;
  }

  async create(settings: ReminderSettings): Promise<ReminderSettings> {
    const map = await this.getMap();
    map[settings.id] = settings;
    await this.setMap(map);
    return settings;
  }

  async update(id: string, settings: Partial<ReminderSettings>): Promise<ReminderSettings> {
    const map = await this.getMap();
    const current = map[id];
    if (!current) throw new Error('Reminder settings not found');
    const updated = { ...current, ...settings, id };
    map[id] = updated;
    await this.setMap(map);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const map = await this.getMap();
    delete map[id];
    await this.setMap(map);
  }

  async clear(): Promise<void> {
    await store.removeItem(CACHE_KEY);
  }
}
